import React from "react";
import { BadgeCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MetallicSheen } from "@/components/common/MetallicSheen";

// Identity marks next to a name. Field-driven: pass the serializer's verified_type
// ("official" | "member" | null). verified_type is computed live from role +
// subscription tier, so a Don who downgrades to Boss or cancels loses BOTH the purple
// name and the member check on the next load. No separate stored flag to go stale.
//
// Two distinct marks on purpose (identity/impersonation work):
//   official -> blue check, name in the normal colour. "This really is the artist."
//   member   -> purple name + purple check. "This is a Don member" - a different
//               colour so a paid near-name cannot borrow the artist's blue signal.
//
// The colour and the check always attach to the DISPLAY NAME the surface renders
// (never the @handle), so the Don perk reads "purple name".
export const MEMBER_PURPLE = "#7C3AED";
const OFFICIAL_BLUE = "#2575FC";

export const markColor = (mark) => (mark === "member" ? MEMBER_PURPLE : OFFICIAL_BLUE);

// Style to apply to the display-name element itself. Only members get recoloured.
export const nameStyleForMark = (mark) => (mark === "member" ? { color: MEMBER_PURPLE } : null);

// Icon-only mark, for appending after a display name that a surface already renders
// (post cards, comments, DM header, profile header). Renders nothing without a mark.
export const VerifiedCheck = ({ mark = null, size = 16, className = "", testid = "verified-check" }) => {
  const { t } = useTranslation();
  if (mark !== "official" && mark !== "member") return null;
  const color = markColor(mark);
  const label = mark === "member"
    ? t("verified.member", { defaultValue: "Don member" })
    : t("verified.official", { defaultValue: "Verified account" });
  // Metallic sheen composites over the coloured check (blue official, purple Don),
  // reusing the one metallic system. Live from `mark`, so it disappears on downgrade.
  return (
    <span className={`relative inline-flex flex-shrink-0 ${className}`} style={{ width: size, height: size }} aria-label={label} data-testid={`${testid}-${mark}`}>
      <BadgeCheck
        size={size}
        className="inline-block"
        style={{ color, fill: color, stroke: "#ffffff" }}
      />
      <MetallicSheen radius="50%" blend="soft-light" />
    </span>
  );
};

// Full display name + mark as plain text. Takes the exact string to show as `name`
// (explicit, no guessing between display name and handle).
export const VerifiedUsername = ({ name, mark = null, size = 16, className = "", style = {}, testid = "verified-username" }) => (
  <span className={`inline-flex items-center ${className}`} style={{ gap: 3, ...style }} data-testid={testid}>
    <span className="relative" style={{ ...(nameStyleForMark(mark) || {}), fontWeight: mark ? 600 : 500 }} data-testid={`${testid}-name`}>
      {name}
      {mark === "member" && <MetallicSheen radius="4px" blend="soft-light" />}
    </span>
    <VerifiedCheck mark={mark} size={size} testid={`${testid}-check`} />
  </span>
);

export default VerifiedUsername;
